<template>
	<ChipsComponent
		:chips="props.chips"
		:controls="props.controls"
		:mixpanel-context="props.mixpanelContext"
		:mixpanel-custom-properties="props.mixpanelCustomProperties"
		:selected="props.selected"
		:scrolls-on-mobile="props.scrollsOnMobile"
		:scrolls-on-overflow="props.scrollsOnOverflow"
		:is-pro="props.isPro"
		:size="props.size"
		:model-value="props.modelValue"
		class="vcx-chips-component"
		@update:model-value="emit('update:modelValue', $event)"
	/>
</template>

<script setup lang="ts">
import { Chip, ChipsSize } from 'types/layout';
import ChipsComponent from '@components/chips/chips-component.vue';
import { MixpanelContext } from 'types/analytics';

interface Props {
	controls?: string;
	chips: Array<Chip>;
	mixpanelContext?: string;
	mixpanelCustomProperties?: MixpanelContext;
	selected?: Chip | null;
	scrollsOnMobile?: boolean;
	scrollsOnOverflow?: boolean;
	isPro?: boolean;
	size?: ChipsSize;
	modelValue?: Chip | null;
}

interface Emits {
	(e: 'update:modelValue', value: Chip | undefined): void;
}

const props = withDefaults(defineProps<Props>(), {
	controls: undefined,
	mixpanelContext: undefined,
	mixpanelCustomProperties: undefined,
	selected: undefined,
	scrollsOnMobile: false,
	scrollsOnOverflow: false,
	isPro: false,
	size: 'sm',
	modelValue: undefined
});

const emit = defineEmits<Emits>();
</script>

<style lang="scss" scoped>
.vcx-chips-component {
	display: flex;
	flex-wrap: wrap;
	gap: 0.5rem;

	:deep(.chip) {
		font-size: var(--vcx-font-size-body-md);
		line-height: var(--vcx-line-height-body-md);
		font-family: var(--vcx-font-body), serif;
		position: relative;
		display: flex;
		align-items: center;
		margin: 0;
		max-height: 36px;
		padding: 6px 16px;
		background-color: transparent;
		border: 1px solid var(--vcx-gray-50);
		border-radius: 35px;
		color: var(--vcx-gray-40);
		cursor: pointer;
		font-weight: 400;
		letter-spacing: normal;
		transition: 0.15s all ease;

		&.selected,
		&.hover,
		&:hover,
		&.focus-visible,
		&:focus-visible {
			border-color: var(--vcx-gray-20);
		}

		&.selected,
		&.hover,
		&:hover {
			background: var(--vcx-gray-80);
			color: var(--vcx-gray-white);
		}

		&.disabled,
		&[disabled] {
			background-color: transparent;
			border-color: var(--vcx-gray-50);
			color: var(--vcx-gray-50);
			opacity: 0.5;
			cursor: not-allowed;

			&:hover,
			&.active {
				border-color: var(--vcx-gray-50);
			}
		}

		&.chip-pro {
			&.selected,
			&.hover,
			&:hover,
			&.focus-visible,
			&:focus-visible {
				border-color: var(--vcx-gray-20);
			}

			&.selected {
				background-color: var(--vcx-bg-dark-secondary);
			}

			&.focus-visible,
			&:focus-visible {
				outline: none;
			}

			&.disabled,
			&[disabled] {
				background-color: transparent;
				border-color: var(--vcx-gray-50);
				color: var(--vcx-gray-50);
				opacity: 0.5;

				&:hover,
				&.active {
					border-color: var(--vcx-gray-50);
				}
			}
		}
	}

	:deep(.chips--xs .chip) {
		font-size: var(--vcx-font-size-body-sm);
		line-height: var(--vcx-line-height-body-sm);
		padding: 5px 12px;
	}

	:deep(.chip-content) {
		display: flex;
		align-items: center;
	}

	:deep(.chip-icon) {
		margin-right: 0.5rem;
	}
}
</style>
